import * as scp from '../../consts/scp/scpFrames';
import DEVICE_TYPES from '../../consts/device/deviceTypes';
import connectivityService from '../../services/device/connectivityService';
import devicePlatformService from '../../services/device/devicePlatformService';
import converter from '../../utils/converter';
import helpers from '../../utils/helpers';
import stringUtils from '../../utils/stringUtils';
import framesProcessingService from '../device/framesProcessingService';
import {log} from '../log';
import {calculateChecksum} from './crcService';
import {READ_HOLDER_SERIAL_NUMBER_TIMEOUT_MS_BLE, READ_HOLDER_SERIAL_NUMBER_TIMEOUT_MS_USB} from './scpConsts';

const GENERAL_READING_TIMEOUT_USB_SEC = 20;
const GENERAL_READING_TIMEOUT_BLE_SEC = 60;

let scpReadingState;

const getEmptyState = () => {
    return {
        type: undefined,
        codentify: undefined,
        serial: undefined,
        supportedCommands: {
            codentify: false,
            serial: false,
        },
        readingStatus: {
            serialReadStarted: false,
            serialReadFinished: false,
            codentifyReadStarted: false,
            codentifyReadFinished: false,
        },
    };
};

const getHolderState = () => {
    if (!scpReadingState.holder) return getEmptyState();

    return {
        ...scpReadingState.holder,
        ...{
            type: undefined,
            codentify: undefined,
            serial: undefined,
            readingStatus: {
                serialReadStarted: false,
                serialReadFinished: false,
                codentifyReadStarted: !scpReadingState.holder.supportedCommands.codentify,
                codentifyReadFinished: !scpReadingState.holder.supportedCommands.codentify,
            },
        },
    };
};

const resetState = (isHolderOnly) => {
    scpReadingState = {
        charger: isHolderOnly ? scpReadingState.charger : getEmptyState(),
        holder: isHolderOnly ? getHolderState() : getEmptyState(),
    };
};

const identifyDevice = async ({isHolderOnly, readHolderSerialNumberTimeoutMs}) => {
    const isBle = connectivityService.isBle();
    const timeout = isBle ? GENERAL_READING_TIMEOUT_BLE_SEC : GENERAL_READING_TIMEOUT_USB_SEC;

    resetState(isHolderOnly);

    //timeout inside is 100ms instead of 1sec
    await waitSupportedDataReadFinish({timer: timeout * 10, readHolderSerialNumberTimeoutMs});

    return mapResponseData(scpReadingState);
};

const mapResponseData = (scpData) => {
    if (!scpData) return;

    const {holder, charger} = scpData;
    const {type, codentify, serial} = charger;
    const {type: holderType, codentify: holderCodentify, serial: holderSerial, supportedCommands} = holder;
    const {serial: isChargerWithHolder} = supportedCommands;
    const chargerData = {charger: {type, codentify, serial}};

    if (isChargerWithHolder) {
        return {...chargerData, holder: {type: holderType, codentify: holderCodentify, serial: holderSerial}};
    } else {
        return chargerData;
    }
};

const waitSupportedDataReadFinish = async ({timer, readHolderSerialNumberTimeoutMs}) => {
    const holderStatus = {...scpReadingState.holder.readingStatus};
    const chargerStatus = {...scpReadingState.charger.readingStatus};

    const allDataReady =
        holderStatus.serialReadFinished &&
        chargerStatus.serialReadFinished &&
        chargerStatus.codentifyReadFinished &&
        holderStatus.codentifyReadFinished;

    if (allDataReady) {
        return;
    }

    readSupportedData({readHolderSerialNumberTimeoutMs});

    if (timer-- < 0) {
        log.info('SCP Service. General data reading timeout exceeded');
        framesProcessingService.clear();
        return;
    }

    await helpers.timeout(100);
    await waitSupportedDataReadFinish({timer});
};

const readSupportedData = ({readHolderSerialNumberTimeoutMs} = {}) => {
    if (toStartChargerSerialNumberReading()) {
        scpReadingState.charger.readingStatus.serialReadStarted = true;
        return readSerialCommon({readHolderSerialNumberTimeoutMs});
    }

    if (isDeviceIdentificationFinished() && toStartChargerCodentifyReading()) {
        scpReadingState.charger.readingStatus.codentifyReadStarted = true;
        return readCodentifyCommon();
    }

    if (toStartHolderSerialNumberReading()) {
        scpReadingState.holder.readingStatus.serialReadStarted = true;
        return readSerialCommon({isHolder: true, readHolderSerialNumberTimeoutMs});
    }

    if (isHolderIdentificationFinished() && toStartHolderCodentifyReading()) {
        scpReadingState.holder.readingStatus.codentifyReadStarted = true;
        return readCodentifyCommon(true);
    }
};

const initDeviceCapabilitites = () => {
    const charger = scpReadingState.charger;
    const holder = scpReadingState.holder;

    charger.supportedCommands.serial = true;
    switch (charger.type) {
        case DEVICE_TYPES.DEV_TYPE_P1V30M:
            holder.supportedCommands.codentify = false;
            holder.supportedCommands.serial = false;
            charger.supportedCommands.codentify = false;
            break;
        case DEVICE_TYPES.DEV_TYPE_P4_M3_G2:
        case DEVICE_TYPES.DEV_TYPE_P1V40D_CONTROL:
        case DEVICE_TYPES.DEV_TYPE_P1V31M:
        case DEVICE_TYPES.DEV_TYPE_P1V41J_CONTROL:
        case DEVICE_TYPES.DEV_TYPE_P1V41A_CONTROL:
            holder.supportedCommands.codentify = false;
            holder.supportedCommands.serial = false;
            charger.supportedCommands.codentify = true;
            break;
        case DEVICE_TYPES.DEV_TYPE_P1_ARIANE:
        case DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER:
        case DEVICE_TYPES.DEV_TYPE_P1V31_HOLDER:
        case DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER:
        case DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER:
        case DEVICE_TYPES.DEV_TYPE_P1V30_CHARGER:
        case DEVICE_TYPES.DEV_TYPE_P1V30_HOLDER:
        case DEVICE_TYPES.DEV_TYPE_P1V40V_CHARGER:
        case DEVICE_TYPES.DEV_TYPE_P1V40C_CHARGER:
        case DEVICE_TYPES.DEV_TYPE_P1V41P_CHARGER:
        case DEVICE_TYPES.DEV_TYPE_P1V41M_CHARGER:
            holder.supportedCommands.codentify = true;
            holder.supportedCommands.serial = true;
            charger.supportedCommands.codentify = true;
            break;
        case DEVICE_TYPES.DEV_TYPE_V24P_CHARGER:
        case DEVICE_TYPES.DEV_TYPE_V24P_HOLDER:
            holder.supportedCommands.codentify = false;
            holder.supportedCommands.serial = true;
            charger.supportedCommands.codentify = false;
            break;
        default:
            break;
    }
    holder.readingStatus.codentifyReadFinished = !holder.supportedCommands.codentify;
    holder.readingStatus.serialReadFinished = !holder.supportedCommands.serial;
    charger.readingStatus.codentifyReadFinished = !charger.supportedCommands.codentify;
};

const getResponseTimeout = ({isHolder, readHolderSerialNumberTimeoutMs}) => {
    const isUsb = connectivityService.isUsb();

    if (!isHolder) return undefined;

    if (readHolderSerialNumberTimeoutMs !== undefined) return readHolderSerialNumberTimeoutMs;

    if (isUsb) {
        return READ_HOLDER_SERIAL_NUMBER_TIMEOUT_MS_USB;
    } else {
        return READ_HOLDER_SERIAL_NUMBER_TIMEOUT_MS_BLE;
    }
};

const readSerialCommon = ({isHolder, readHolderSerialNumberTimeoutMs} = {}) => {
    const device = isHolder ? scpReadingState.holder : scpReadingState.charger;

    device.readingStatus.serialReadFinished = false;

    const isUsb = connectivityService.isUsb();
    let frame;

    if (isUsb) {
        frame = isHolder ? scp.FRAME_HOLDER_SERIAL_USB : scp.FRAME_CHARGER_SERIAL_USB;
    } else {
        frame = isHolder ? scp.FRAME_HOLDER_SERIAL : scp.FRAME_CHARGER_SERIAL;
    }

    const READ_HOLDER_SERIAL_NUMBER_RETRY_COUNT = 1;
    const responseTimeoutMs = getResponseTimeout({isHolder, readHolderSerialNumberTimeoutMs});
    const retryCount = isHolder ? READ_HOLDER_SERIAL_NUMBER_RETRY_COUNT : undefined;

    return sendFrame({
        frame,
        onSuccess: (frames) => {
            const frame = mapResponseFrame(converter.buffer2hexArr(frames[0]));

            if (frame.isError || !frame.isCrcValid) {
                return onCommonReadError(isHolder);
            }

            const deviceSerial = mapSerialNumberFramePayload(frame.payload);

            const upperCaseSerialNumber = deviceSerial?.dusn.toUpperCase();

            device.serial = '0' + upperCaseSerialNumber.charAt(1).toLowerCase() + upperCaseSerialNumber.slice(2); // need transform device serial number in right format

            setDeviceTypeBySerial(device.serial, frame.isHolderResponse);

            if (frame.isChargerResponse) {
                initDeviceCapabilitites();

                if (device.type === DEVICE_TYPES.DEV_TYPE_UNKNOWN) {
                    onCommonReadError();
                }
            }

            device.readingStatus.serialReadFinished = true;

            readSupportedData({readHolderSerialNumberTimeoutMs});
        },
        onError: () => onCommonReadError(isHolder),
        responseTimeoutMs,
        retryCount,
    });
};

const readCodentifyCommon = (isHolder) => {
    const device = isHolder ? scpReadingState.holder : scpReadingState.charger;

    device.readingStatus.codentifyReadFinished = false;

    let frame;

    if (connectivityService.isUsb()) {
        frame = isHolder ? scp.FRAME_HOLDER_CODENTIFY_USB : scp.FRAME_CHARGER_CODENTIFY_USB;
    } else {
        frame = isHolder ? scp.FRAME_HOLDER_CODENTIFY : scp.FRAME_CHARGER_CODENTIFY;
    }

    return sendFrame({
        frame,
        onSuccess: (frames) => {
            const frame = mapResponseFrame(converter.buffer2hexArr(frames[0]));

            if (frame.isError || !frame.isCrcValid) {
                return onCommonReadError(isHolder, true);
            }

            const codentify = String.fromCharCode.apply(null, frame.payload);

            if (stringUtils.isAlphaNumeric(codentify)) {
                device.codentify = codentify.toUpperCase();
            }
            device.readingStatus.codentifyReadFinished = true;

            readSupportedData();
        },
        onError: () => onCommonReadError(isHolder, true),
    });
};

const onCommonReadError = (isHolder, isCodentify) => {
    if (!isHolder) {
        scpReadingState.charger.readingStatus.serialReadFinished = true;
        scpReadingState.charger.readingStatus.codentifyReadFinished = true;
    }

    if (isHolder || !isCodentify) {
        //don't read all other holder data w/o charger serial or holder serial (if isHolder = true)
        scpReadingState.holder.readingStatus.serialReadFinished = true;
        scpReadingState.holder.readingStatus.codentifyReadFinished = true;
        return;
    }

    readSupportedData();
};

const isDeviceIdentificationFinished = () => scpReadingState.charger.readingStatus.serialReadFinished;

const isHolderIdentificationFinished = () => scpReadingState.holder.readingStatus.serialReadFinished;

const toStartChargerSerialNumberReading = () => {
    return (
        !scpReadingState.charger.readingStatus.serialReadStarted &&
        !scpReadingState.charger.readingStatus.serialReadFinished
    );
};

const toStartHolderSerialNumberReading = () => {
    return (
        scpReadingState.holder.supportedCommands.serial &&
        !scpReadingState.holder.readingStatus.serialReadStarted &&
        !scpReadingState.holder.readingStatus.serialReadFinished
    );
};

const toStartChargerCodentifyReading = () => {
    return (
        scpReadingState.charger.supportedCommands.codentify &&
        !scpReadingState.charger.readingStatus.codentifyReadStarted &&
        !scpReadingState.charger.readingStatus.codentifyReadFinished
    );
};

const toStartHolderCodentifyReading = () => {
    return (
        scpReadingState.holder.supportedCommands.codentify &&
        !scpReadingState.holder.readingStatus.codentifyReadFinished &&
        !scpReadingState.holder.readingStatus.codentifyReadStarted
    );
};

const sendFrame = ({frame, onSuccess, onError, responseTimeoutMs, retryCount}) => {
    framesProcessingService.writeFrameAndPublishResponse({
        frames: [frame],
        onSuccess: (...frames) => {
            onSuccess(frames);
        },
        onError,
        responseTimeoutMs,
        retryCount,
    });
};

const mapResponseFrame = (frame) => {
    const isUsb = connectivityService.isUsb();

    if (isUsb) {
        //skip usb header
        frame.shift();
    }

    let mappedFrame = {
        isError: (frame[scp.OFFSET_APP_LAYER_HEADER] & (1 << 5)) !== 0,
        isChargerResponse: frame[scp.OFFSET_DEVICE_TYPE] === scp.HEADER_CHARGER_RESPONSE,
        isHolderResponse: frame[scp.OFFSET_DEVICE_TYPE] === scp.HEADER_HOLDER_RESPONSE,
        frameHeader: frame.slice(scp.OFFSET_APP_LAYER_HEADER, scp.OFFSET_PAYLOAD_START),
        payload: frame.slice(scp.OFFSET_PAYLOAD_START, isUsb ? frame[0] : frame.length - 1),
        crc: isUsb ? frame[frame[0]] : frame[frame.length - 1],
    };

    mappedFrame = {...mappedFrame, isCrcValid: isValidCrc(mappedFrame)};

    if (!mappedFrame.isCrcValid) {
        log.error(`ScpReadingService. Frame CRC ${mappedFrame?.crc} is not valid.`);
    }

    return mappedFrame;
};

const mapSerialNumberFramePayload = (payload) => {
    if (!payload) {
        return;
    }

    const deviceSerial = {
        platformCode: undefined,
        productCode: undefined,
        siteCode: undefined,
        deviceNumber: undefined,
        dusn: undefined,
    };

    (deviceSerial.platformCode = payload.slice(scp.PLATFORM_CODE_OFFSET, scp.PRODUCT_CODE_OFFSET).reverse()),
        (deviceSerial.productCode = payload.slice(scp.PRODUCT_CODE_OFFSET, scp.SITE_CODE_OFFSET).reverse()),
        (deviceSerial.siteCode = payload.slice(scp.SITE_CODE_OFFSET, scp.DEVICE_NUMBER_OFFSET).reverse()),
        (deviceSerial.deviceNumber = payload.slice(scp.DEVICE_NUMBER_OFFSET, scp.DEVICE_NUMBER_END).reverse());
    deviceSerial.dusn = [
        '0x',
        deviceSerial.platformCode.map((x) => x.toString(16).padStart(2, '0')).join(''),
        deviceSerial.productCode.map((x) => x.toString(16).padStart(2, '0')).join(''),
        deviceSerial.siteCode.map((x) => x.toString(16).padStart(2, '0')).join(''),
        deviceSerial.deviceNumber.map((x) => x.toString(16).padStart(2, '0')).join(''),
    ].join('');

    return deviceSerial;
};

const isValidCrc = (frame) => {
    if (frame.crc != null && frame.payload != null) {
        const calculatedCrc = calculateChecksum([...frame.frameHeader, ...frame.payload]);

        return calculatedCrc === frame.crc;
    } else {
        return false;
    }
};

const setDeviceTypeBySerial = (serialNumber, isHolder) => {
    if (!serialNumber) {
        return;
    }

    const device = isHolder ? scpReadingState.holder : scpReadingState.charger;

    device.type = devicePlatformService.getDeviceTypeBySerialNumber(serialNumber);
};

export default {
    identifyDevice,
};
